<script setup lang="ts">
import {computed, reactive, ref} from "vue";
import TcVod from "vod-js-sdk-v6";
import Uploader from "vod-js-sdk-v6/lib/src/uploader";
import {ComponentSize, dayjs, ElNotification, FormInstance, FormRules} from "element-plus";
import {CustomFile, SaveLiveSection, SectionInfo} from "@/types";
import {VideoService} from "@/api/api.ts";
import AddSingleFile from "components/dialog/common/innner/AddSingleFile.vue";
import AddFakeLiveVideo from "components/dialog/live/inner/AddFakeLiveVideo.vue";
import {TimeFormatter} from "@/utils/common.ts";

const aheadRef = ref<InstanceType<typeof AddSingleFile>>();
const pdfRef = ref<InstanceType<typeof AddSingleFile>>();
const consolidateRef = ref<InstanceType<typeof AddSingleFile>>();
const addFakeLiveVideo = ref<InstanceType<typeof AddFakeLiveVideo>>()

const props = defineProps<{
  is_fake: boolean
}>()

interface RuleForm {
  title: string
}

const canSetRecord = ref(false)
const is_playback = ref(0)
const videoId = ref('')
const liveDate = ref<Date>()
const startTime = ref('')
const endTime = ref('')
const view_start_time = ref<Date>()
const view_end_time = ref<Date>()

let initData: SectionInfo | undefined

let uploader: Uploader;
// 0: 未上传 1: 上传中 2: 上传成功 3: 上传失败
const uploadStatus = ref(0);
const mediaProgress = ref(0)
const videoDuration = ref(0)

const formSize = ref<ComponentSize>('default')
const ruleFormRef = ref<FormInstance>()
const ruleForm = reactive<RuleForm>({
  title: '',
})
const rules = reactive<FormRules<RuleForm>>({
  title: [
    {required: true, message: '请输入章节标题', trigger: 'blur'},
    {min: 1, max: 30, message: '标题长度在 1 到 30 个字符', trigger: 'blur'},
  ]
})
const emit = defineEmits<{
  (e: "onSubmit", info: SaveLiveSection): void
  (e: "onClose"): void
}>()

const getSignature = async (): Promise<string> => {
  const res = await VideoService.createSignature()
  return res.data.data || ''
}

const tcVod = new TcVod({
  getSignature: getSignature,
})

const onClear = () => {
  videoId.value = ''
}

const cancelUpload = () => {
  if (uploader) {
    uploader.cancel()
  }
  mediaProgress.value = 0
  uploadStatus.value = 0
}

const onVideoFileChange = (file: CustomFile) => {
  if (checkVideoDuration(file)) {
    addFakeLiveVideo.value?.onStartUpload(file)
    upload(file)
  }
}

const upload = (file: CustomFile) => {
  // 视频名称
  if (uploader) {
    uploader.cancel()
  }
  uploader = tcVod.upload({
    mediaFile: file,
  })
  uploadStatus.value = 1
  uploader.on("media_progress", info => {
    // 上传进度
    mediaProgress.value = Math.floor(info.percent * 100);
    console.log(mediaProgress.value);
  })
  uploader.on("media_upload", info => {
    // 上传成功
    console.log(info)
  })
  uploader.done().then(res => {
    const id = res.fileId
    // 提交视频
    VideoService.addVideo({
      file_name: file.name,
      video_id: id,
      duration: file.duration || 0,
      width: file.width || 0,
      height: file.height || 0,
    }).then(res => {
      if (res.data.code === 200) {
        videoId.value = id
        uploadStatus.value = 2
        ElNotification.success({
          title: "视频上传成功",
          message: res.data.msg,
          type: "success",
        })
      } else {
        ElNotification.error({
          title: "提示",
          message: res.data.msg,
          type: "error",
        })
      }
    }).catch(err => {
      console.log(err)
      uploadStatus.value = 3
    })
  }).catch(err => {
    console.log(err)
    uploadStatus.value = 3
  })
}

const isLiveDateValid = (): boolean => {
  if (!liveDate.value) {
    ElNotification.warning({
      title: "提示",
      message: "请选择直播日期",
      type: "warning",
    })
    return false
  }
  return true
}

const isLiveTimeValid = (): boolean => {
  if (!startTime.value) {
    ElNotification.warning({
      title: "提示",
      message: "请选择直播开始时间",
      type: "warning",
    })
    return false
  }
  if (!endTime.value) {
    ElNotification.warning({
      title: "提示",
      message: "请选择直播结束时间",
      type: "warning",
    })
    return false
  }
  if (props.is_fake && videoDuration.value !== 0) {
    const start = dayjs(startTime.value, "HH:mm").unix()
    const end = dayjs(endTime.value, "HH:mm").unix()
    const getLiveDuration = end - start
    if (Math.abs(getLiveDuration - videoDuration.value) > 3 * 60) {
      ElNotification.warning({
        title: "提示",
        message: "直播时长与视频时长误差不能超过 3 分钟",
        type: "warning",
      })
      return false
    }
  }
  return true
}

const checkVideoDuration = (file: CustomFile) => {
  if (startTime.value && endTime.value) {
    videoDuration.value = file.duration || 0
    const start = dayjs(startTime.value, "HH:mm").unix()
    const end = dayjs(endTime.value, "HH:mm").unix()
    const getLiveDuration = end - start
    if (Math.abs(getLiveDuration - videoDuration.value) > 3 * 60) {
      videoDuration.value = 0
      ElNotification.warning({
        title: "提示",
        message: "直播时长与视频时长误差不能超过 3 分钟",
        type: "warning",
      })
      return false
    }
    return true
  }
}

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return
  await formEl.validate((valid, fields) => {
    if (valid) {
      if (isLiveDateValid() && isLiveTimeValid()) {
        const liveDateUnix = dayjs(liveDate.value).startOf('day').unix()
        const startSub = dayjs(startTime.value, 'HH:mm').startOf('minutes').unix() - dayjs().startOf('day').unix()
        const endSub = dayjs(endTime.value, 'HH:mm').startOf('minutes').unix() - dayjs().startOf('day').unix()
        const startTimeUnix = liveDateUnix + startSub
        const endTimeUnix = liveDateUnix + endSub

        let viewStartTime = undefined, viewEndTime = undefined
        if (is_playback.value === 1) {
          if (view_start_time.value) {
            viewStartTime = dayjs(view_start_time.value).unix()
          }
          if (view_end_time.value) {
            viewEndTime = dayjs(view_end_time.value).unix()
          }
        }
        emit('onSubmit', {
          title: ruleForm.title,
          live_date: initData?.live_date == liveDateUnix ? undefined : liveDateUnix,
          start_time: initData?.start_time == startTimeUnix ? undefined : startTimeUnix,
          end_time: initData?.end_time == endTimeUnix ? endTimeUnix : endTimeUnix,
          video_id: videoId.value,
          ahead_data: aheadRef.value?.getFileId(),
          pdf_data: pdfRef.value?.getFileId(),
          consolidate_data: consolidateRef.value?.getFileId(),
          is_playback: is_playback.value,
          view_start_time: viewStartTime,
          view_end_time: viewEndTime,
        })
      }
    } else {
      console.log('error submit!', fields)
    }
  })
}

const resetForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return
  formEl.resetFields()
  emit("onClose")
}

const init = (sectionInfo?: SectionInfo) => {
  initData = sectionInfo
  if (sectionInfo) {
    ruleForm.title = sectionInfo.title
    liveDate.value = new Date(sectionInfo.live_date * 1000)
    startTime.value = dayjs(sectionInfo.start_time * 1000).format('HH:mm')
    endTime.value = dayjs(sectionInfo.end_time * 1000).format('HH:mm')
    if (props.is_fake) {
      canSetRecord.value = dayjs().unix() > sectionInfo.end_time
    } else {
      canSetRecord.value = sectionInfo.status === 1
    }
    is_playback.value = sectionInfo.is_playback
    if (sectionInfo.view_start_time) {
      view_start_time.value = new Date(sectionInfo.view_start_time * 1000)
    }
    if (sectionInfo.view_end_time) {
      view_end_time.value = new Date(sectionInfo.view_end_time * 1000)
    }
    addFakeLiveVideo.value?.init("已上传")
    uploadStatus.value = 2
    aheadRef.value?.initFileList(sectionInfo.ahead_data)
    pdfRef.value?.initFileList(sectionInfo.pdf_data)
    consolidateRef.value?.initFileList(sectionInfo.consolidate_data)
  }
}

const needDuration = computed(() => {
  if (startTime.value && endTime.value) {
    const start = dayjs(startTime.value, "HH:mm").unix()
    const end = dayjs(endTime.value, "HH:mm").unix()
    const liveDuration = end - start
    return (
        TimeFormatter.secondsFormat(liveDuration - 180) +
        "~" +
        TimeFormatter.secondsFormat(liveDuration + 180)
    );
  }
  return "待定";
});

defineExpose({cancelUpload, init})
</script>

<template>
  <el-form
      ref="ruleFormRef"
      style="max-width: 40rem"
      :model="ruleForm"
      :rules="rules"
      label-width="auto"
      class="demo-ruleForm"
      :size="formSize"
      status-icon
  >
    <el-form-item label="章节标题" prop="title">
      <el-input v-model="ruleForm.title" placeholder="请输入章节标题" maxlength="30" show-word-limit/>
    </el-form-item>
    <el-form-item label="直播日期">
      <div>
        <div>
          <el-date-picker placeholder="直播日期" v-model="liveDate" type="date"/>
        </div>
        <div class="mt-1rem">
          <el-time-select
              v-model="startTime"
              style="width: 240px"
              :max-time="endTime"
              class="mr-1rem"
              placeholder="直播开始时间"
              start="06:00"
              step="00:05"
              end="23:00"
          />
          <el-time-select
              v-model="endTime"
              style="width: 240px"
              :min-time="startTime"
              placeholder="直播结束时间"
              start="06:00"
              step="00:05"
              end="23:00"
          />
        </div>
        <div class="mt-.5rem font-bold" v-if="is_fake">
          请谨慎更改上课时间,根据您选择的上课时间，您需要上传时长范围为
          <span class="text-red-4">
              {{ needDuration }}
            </span>
          的视频
        </div>
      </div>
    </el-form-item>
    <el-form-item label="直播视频" v-if="is_fake">
      <add-fake-live-video
          ref="addFakeLiveVideo"
          :uploadStatus="uploadStatus"
          :video-progress="mediaProgress"
          @on-clear="onClear"
          @on-cancel-upload="cancelUpload"
          @on-video-file-change="onVideoFileChange"/>
    </el-form-item>
    <el-form-item label="回放设置" v-if="canSetRecord">
      <el-radio-group v-model="is_playback">
        <el-radio-button label="有时效" :value="1"/>
        <el-radio-button label="无时效" :value="0"/>
      </el-radio-group>
    </el-form-item>
    <el-form-item label="回放时间" v-if="canSetRecord && is_playback === 1">
      <el-date-picker
          v-model="view_start_time"
          type="datetime"
          format="YYYY-MM-DD HH:mm"
          placeholder="请选择开始时间(可不填)"
      />
      <span class="ml-1rem mr-1rem">~</span>
      <el-date-picker
          v-model="view_end_time"
          type="datetime"
          format="YYYY-MM-DD HH:mm"
          placeholder="请选择结束时间(可不填)"
      />
    </el-form-item>
    <el-form-item label="上传导学">
      <add-single-file ref="aheadRef"></add-single-file>
    </el-form-item>
    <el-form-item label="上传讲义">
      <add-single-file ref="pdfRef"></add-single-file>
    </el-form-item>
    <el-form-item label="上传巩固">
      <add-single-file ref="consolidateRef"></add-single-file>
    </el-form-item>
    <el-form-item>
      <el-button type="primary" @click="submitForm(ruleFormRef)">
        修改信息
      </el-button>
      <el-button @click="resetForm(ruleFormRef)">取消</el-button>
    </el-form-item>
  </el-form>
</template>

<style scoped>
</style>
